// Whether the API answers (task 2447, Mark 2026-10-15). When it is restarting or unreachable,
// BOB shows ONE whole-screen message instead of each screen's own "Failed to fetch": the shared
// fetch (plugins/auth-fetch.client.ts) marks it down, holds its reads until it is back and then
// sends them again, and this asks the API on its own every few seconds until it answers. Any
// answer at all, even a refusal, means it is up. The message is drawn by the plugin straight into
// the page, not by a component: at start-up the screen waits on the API (who is logged in) before
// any component is drawn, and the message must show then too.

const PROBE_EVERY_MS = 3000
// A tablet waking from sleep sends its first requests before its Wi-Fi or cellular link is back,
// so for a moment nothing answers (Mark, 2026-10-22: the coffee-break card "for a little bit every
// time"). The reads are held at once, but the message waits this long and shows only if the API is
// still not answering; the first ask goes sooner, and again the moment the device is back online or
// the screen is shown again.
const SHOW_AFTER_MS = 4000
const FIRST_ASK_MS = 1000

let down = false
let shown = false
let showTimer: ReturnType<typeof setTimeout> | undefined
let waiters: Array<() => void> = []
let probing = false
let pending: ReturnType<typeof setTimeout> | undefined
let askAgain: (() => void) | null = null
let onChange: (isDown: boolean) => void = () => {}

/** What to do when the API stops or starts answering: the plugin shows or takes away the message. */
export function onServerDownChange(listener: (isDown: boolean) => void): void {
  onChange = listener
}

/**
 * The API did not answer: show the message, and start asking until it does. `probe` is the
 * unpatched fetch of the API's live check, so asking never waits on itself.
 */
export function markServerDown(probe: () => Promise<Response>): void {
  if (!down) {
    down = true
    showTimer = setTimeout(() => {
      if (!down) return
      shown = true
      onChange(true)
    }, SHOW_AFTER_MS)
  }
  if (probing) return
  probing = true
  const ask = async () => {
    try {
      await probe()
      // An answer of any kind: the server is back.
      probing = false
      askAgain = null
      down = false
      clearTimeout(showTimer)
      if (shown) {
        shown = false
        onChange(false)
      }
      const ready = waiters
      waiters = []
      ready.forEach(resolve => resolve())
      return
    } catch {
      // Still not answering.
    }
    pending = setTimeout(ask, PROBE_EVERY_MS)
  }
  askAgain = () => {
    clearTimeout(pending)
    void ask()
  }
  pending = setTimeout(ask, FIRST_ASK_MS)
}

// Back online, or the screen shown again after sleep: ask straight away rather than at the next check.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => askAgain?.())
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') askAgain?.()
  })
}

/**
 * Try Again on the message (Mark, 2026-10-21, as Arlo's offline card has): ask the API now instead
 * of waiting for the next few-second check. Resolves once that one ask has answered or failed.
 */
export async function askServerNow(): Promise<void> {
  const again = askAgain
  if (!again) return
  again()
  await new Promise(resolve => setTimeout(resolve, 600))
}

/** Resolves when the API answers again; rejects as an abort if `signal` aborts first. */
export function whenServerUp(signal?: AbortSignal | null): Promise<void> {
  if (!down) return Promise.resolve()
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? new DOMException('Aborted', 'AbortError'))
      return
    }
    const done = () => resolve()
    waiters.push(done)
    signal?.addEventListener('abort', () => {
      waiters = waiters.filter(w => w !== done)
      reject(signal.reason ?? new DOMException('Aborted', 'AbortError'))
    }, { once: true })
  })
}

/** A request that never got an answer: the network, not the server, said no. */
export function isUnanswered(error: unknown): boolean {
  return error instanceof TypeError
}

/**
 * What the proxy in front of the API says while the API behind it is restarting. Not 503: the API
 * answers that itself when something it depends on is out (a scan reader), and is up.
 */
export function isGatewayDown(status: number): boolean {
  return status === 502 || status === 504
}
